// 2D view (spec §6): SVG drawn from the SAME outlines the mesh is extruded from, so the edit
// view cannot drift from the print. PURE string building — main.js puts it into the page.
// Model space is millimetres with y UP; SVG is y DOWN. The flip lives in ONE place: a
// `scale(1,-1)` group. modelToSvg/svgToModel are the matching point maps (P2's drag needs them).
import { S } from '../strings.js';

export const VIEW_PAD = 2;   // mm of air around the body

export function viewBoxOf(view) {
  const { minX, minY, maxX, maxY } = view.bounds;
  return { x: minX - VIEW_PAD, y: -(maxY + VIEW_PAD), w: maxX - minX + 2 * VIEW_PAD, h: maxY - minY + 2 * VIEW_PAD };
}
export const modelToSvg = ([x, y]) => [x, -y];
export const svgToModel = ([x, y]) => [x, -y];

const n = (v) => (Math.round(v * 100) / 100).toString();
const pathOf = (polygons) => polygons.map(p => 'M' + p.map(([x, y]) => `${n(x)} ${n(y)}`).join('L') + 'Z').join('');
const esc = (s) => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// D9: the 2D preview has no lighting of its own to show an ENGRAVED face's recess, so it is drawn
// as the tag colour at reduced brightness instead. hex: '#RRGGBB'. factor: fraction of brightness kept.
export function shadeHex(hex, factor = 0.55) {
  const h = String(hex).replace('#', '');
  const c = (o) => Math.max(0, Math.min(255, Math.round(parseInt(h.slice(o, o + 2), 16) * factor))).toString(16).padStart(2, '0');
  return `#${c(0)}${c(2)}${c(4)}`.toUpperCase();
}

// Mixes hex toward white by frac (0..1). Companion to shadeHex (which darkens) — used by
// engraveShade for tags already too dark for darkening to read as a distinct shade.
function mixWhite(hex, frac) {
  const h = String(hex).replace('#', '');
  const c = (o) => Math.max(0, Math.min(255, Math.round(parseInt(h.slice(o, o + 2), 16) * (1 - frac) + 255 * frac))).toString(16).padStart(2, '0');
  return `#${c(0)}${c(2)}${c(4)}`.toUpperCase();
}

// WCAG 2.x relative luminance (0..1) of a '#RRGGBB' colour.
export function relativeLuminance(hex) {
  const h = String(hex).replace('#', '');
  const chan = (o) => {
    const c = parseInt(h.slice(o, o + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * chan(0) + 0.7152 * chan(2) + 0.0722 * chan(4);
}

// WCAG 2.x contrast ratio (1..21) between two '#RRGGBB' colours.
export function contrastRatio(hexA, hexB) {
  const a = relativeLuminance(hexA), b = relativeLuminance(hexB);
  const [hi, lo] = a >= b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

// Bug: shadeHex(tag, 0.55) on an already-dark tag (Black #111111, Filasophy Ink #0A0918, …) darkens
// it further — contrast(tag, shade) ≈ 1, i.e. invisible engraved text. Fix: below the luminance
// threshold, lighten toward white instead of darkening further; at/above it, darken as before. Tuned
// against every FILAMENTS/BRAND tag colour (test/view.test.js) — min contrast found there is ~1.88
// (Red), comfortably over the ≥1.8 target.
const ENGRAVE_LUMINANCE_THRESHOLD = 0.2;
const ENGRAVE_MIX_WHITE = 0.45;
const ENGRAVE_DARKEN = 0.55;
export function engraveShade(tagHex) {
  return relativeLuminance(tagHex) < ENGRAVE_LUMINANCE_THRESHOLD
    ? mixWhite(tagHex, ENGRAVE_MIX_WHITE)
    : shadeHex(tagHex, ENGRAVE_DARKEN);
}

// opts: { fill: css colour of the tag body, text: css colour of the .el elements (D9 — the caller
//   picks textColor for a raised front, engraveShade(fill) for an engraved front or the (always-engraved)
//   back), bad: Set<elId> (outlined red), selected: elId | null }
export function viewToSvg(view, opts = {}) {
  const { fill = '#ff8a3d', text = '#ffffff', bad = new Set(), selected = null } = opts;
  const vb = viewBoxOf(view);
  const back = view.face === 'back';                 // drawn as seen from the back: no rim there (it is a front-face feature)
  const els = view.elements.map(e => {
    const cls = ['el', bad.has(e.id) ? 'bad' : '', e.id === selected ? 'sel' : ''].filter(Boolean).join(' ');
    return `<path class="${cls}" data-el="${esc(e.id)}" fill="${esc(text)}" fill-rule="evenodd" d="${pathOf(e.polygons)}"/>`;
  }).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" class="${back ? 'face-back' : 'face-front'}" viewBox="${n(vb.x)} ${n(vb.y)} ${n(vb.w)} ${n(vb.h)}" role="img" aria-label="${back ? S.ariaPreview.back : S.ariaPreview.front}">`
    + `<g transform="scale(1,-1)">`
    + `<path class="body" fill="${esc(fill)}" fill-rule="evenodd" d="${pathOf(view.body)}"/>`
    + (back ? '' : `<circle class="rim" cx="${n(view.hole.x)}" cy="${n(view.hole.y)}" r="${n(view.hole.rimR)}"/>`)
    + `<circle class="hole" cx="${n(view.hole.x)}" cy="${n(view.hole.y)}" r="${n(view.hole.r)}"/>`
    + els
    + `<g class="guides"></g>`
    + `</g></svg>`;
}

// Snap guide lines (src/editor/snap.js) as SVG, for the `g.guides` group — model coordinates,
// because that group sits inside the y-flip. axis 'x' = a vertical line at x = at.
export function guidesToSvg(guides, view) {
  const { minX, minY, maxX, maxY } = view.bounds;
  return guides.map(g => (g.axis === 'x'
    ? `<line class="guide ${esc(g.kind)}" x1="${n(g.at)}" y1="${n(minY - VIEW_PAD)}" x2="${n(g.at)}" y2="${n(maxY + VIEW_PAD)}"/>`
    : `<line class="guide ${esc(g.kind)}" x1="${n(minX - VIEW_PAD)}" y1="${n(g.at)}" x2="${n(maxX + VIEW_PAD)}" y2="${n(g.at)}"/>`)).join('');
}
